import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { apiError } from "./api";
import { useAuth } from "./QcApp";
import { COUNTRY_CODES } from "./countryCodes";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const titleCase = (s) =>
  (s || "").split(" ").filter(Boolean).map((w) =>
    /[A-Z]/.test(w.slice(1)) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(" ");

const EMPTY = {
  name: "", address: "", email: "", website: "", contact_person: "", phone: "", country: "",
  bank_accounts: [],
};
const EMPTY_BANK = {
  label: "", beneficiary_bank_name: "", swift_bic: "", iban: "", account_no: "", bcn: "", ifsc: "", is_default: true,
};

export function VendorFormDialog({ open, onOpenChange, initial, onSaved, pickOnly = false }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [phoneIso, setPhoneIso] = useState("IN");
  const [phoneNum, setPhoneNum] = useState("");
  const [vendorList, setVendorList] = useState([]);
  const [editId, setEditId] = useState(null);

  const applyPhone = (phone) => {
    const m = (phone || "").match(/^(\+\d{1,4})\s*(.*)$/);
    if (m && m[2].replace(/\D/g, "")) {
      const hit = COUNTRY_CODES.find((c) => c[2] === m[1]);
      setPhoneIso(hit ? hit[0] : "IN");
      setPhoneNum(m[2].replace(/\D/g, ""));
    } else {
      setPhoneIso("IN");
      setPhoneNum((phone || "").replace(/\D/g, ""));
    }
  };

  useEffect(() => {
    if (open) {
      const next = initial ? { ...EMPTY, ...initial } : EMPTY;
      setForm(next);
      applyPhone(next.phone);
      setEditId(initial ? initial.id : null);
      if (initial) {
        api.get("/vendors")
          .then((r) => setVendorList(r.data))
          .catch(() => setVendorList([]));
      }
    }
  }, [open, initial]);

  // Picking a different vendor name switches the dialog to editing THAT vendor's
  // record (details load from the master; Save writes to that vendor, never to
  // the originally opened one — so no record can be overwritten by mistake)
  const pickVendor = (name) => {
    const v = vendorList.find((x) => x.name === name);
    if (v) {
      setForm({ ...EMPTY, ...v });
      applyPhone(v.phone);
      setEditId(v.id);
    } else {
      set("name", name);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBank = (i, k, v) =>
    setForm((f) => {
      const banks = f.bank_accounts.map((b, j) => (j === i ? { ...b, [k]: v } : k === "is_default" && v ? { ...b, is_default: false } : b));
      return { ...f, bank_accounts: banks };
    });

  const save = async () => {
    if (pickOnly) {
      // QC pick-only mode: select a vendor name for the CASE — never writes the vendor record
      const v = vendorList.find((x) => x.name === form.name);
      if (!v) return toast.error("Please select a vendor");
      onSaved(v);
      return;
    }
    if (!form.name.trim()) return toast.error("Vendor name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    if (!form.contact_person.trim()) return toast.error("Contact Person is required");
    if (!phoneNum.trim()) return toast.error("Phone Number is required");
    if (!form.country.trim()) return toast.error("Country is required");
    if (form.bank_accounts.length === 0) return toast.error("Bank details are mandatory — please add at least one bank account");
    const badBank = form.bank_accounts.some((b) => !b.beneficiary_bank_name.trim() || !b.account_no.trim());
    if (badBank) return toast.error("Beneficiary Bank Name and Account No. are mandatory for every bank account");
    setBusy(true);
    try {
      const dial = COUNTRY_CODES.find((c) => c[0] === phoneIso)?.[2] || "+91";
      const payload = {
        ...form,
        name: titleCase(form.name),
        address: titleCase(form.address),
        contact_person: titleCase(form.contact_person),
        country: titleCase(form.country),
        phone: `${dial} ${phoneNum}`,
        bank_accounts: form.bank_accounts.map((b) => ({ ...b, beneficiary_bank_name: titleCase(b.beneficiary_bank_name) })),
      };
      const { data } = editId
        ? await api.put(`/vendors/${editId}`, payload)
        : await api.post("/vendors", payload);
      toast.success(editId ? "Vendor updated" : "Vendor added");
      onSaved(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const allFields = [
    ["name", "Vendor Name", true], ["address", "Address"], ["email", "Email", true], ["website", "Website"],
    ["contact_person", "Contact Person", true], ["phone", "Phone Number", true], ["country", "Country", true],
  ];
  const fields = pickOnly ? allFields.filter(([k]) => k === "name") : allFields;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="qc-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{pickOnly ? "Change Vendor for this Case" : initial ? "Edit Vendor" : "Add Vendor (Lab)"}</DialogTitle>
        </DialogHeader>
        {pickOnly && (
          <p className="text-xs text-[#6B5B3E]" data-testid="qc-vendor-pickonly-hint">
            You can only change the vendor name attached to this case. To update vendor details (address, contact, bank), please ask Accounts or Admin via Vendor Master.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {fields.map(([k, label, required]) => (
            <div key={k} className={`space-y-1 ${k === "address" ? "sm:col-span-2" : ""}`}>
              <Label className="text-xs">{label}{required && <span className="ml-0.5 font-semibold text-red-600">*</span>}</Label>
              {k === "name" && initial ? (
                <>
                  <Select value={form.name || ""} onValueChange={pickVendor}>
                    <SelectTrigger data-testid="qc-vendor-name"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {[...new Set([form.name, ...vendorList.map((v) => v.name)].filter(Boolean))].map((n) => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editId && editId !== initial.id && (
                    <p className="text-xs text-[#7a5b12]" data-testid="qc-vendor-switched-note">
                      {pickOnly ? "This vendor will be attached to the case." : "Switched — saving will update this vendor's own record."}
                    </p>
                  )}
                </>
              ) : k === "phone" ? (
                <div className="flex gap-2">
                  <Select value={phoneIso} onValueChange={setPhoneIso}>
                    <SelectTrigger data-testid="qc-vendor-phone-code" className="w-[140px] shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {COUNTRY_CODES.map(([iso, cname, dial]) => (
                        <SelectItem key={iso} value={iso}>{cname} ({dial})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input data-testid="qc-vendor-phone" inputMode="numeric" placeholder="98765 43210" value={phoneNum}
                    onChange={(e) => setPhoneNum(e.target.value.replace(/\D/g, ""))} />
                </div>
              ) : k === "country" ? (
                <Select value={form.country || ""} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="qc-vendor-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRY_CODES.map(([iso, cname]) => (
                      <SelectItem key={iso} value={cname}>{cname}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`qc-vendor-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)}
                  onBlur={["name", "address", "contact_person", "country"].includes(k) ? (e) => set(k, titleCase(e.target.value)) : undefined} />
              )}
            </div>
          ))}
        </div>
        {!pickOnly && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label>Bank Accounts<span className="ml-0.5 font-semibold text-red-600">*</span></Label>
            <Button type="button" variant="outline" size="sm" data-testid="qc-vendor-add-bank"
              onClick={() => setForm((f) => ({ ...f, bank_accounts: [...f.bank_accounts, { ...EMPTY_BANK, is_default: f.bank_accounts.length === 0 }] }))}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add account
            </Button>
          </div>
          {form.bank_accounts.map((b, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-[#E8E6E1] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox checked={b.is_default} onCheckedChange={(v) => setBank(i, "is_default", !!v)} id={`def-${i}`} />
                  <Label htmlFor={`def-${i}`} className="text-xs">Default account</Label>
                </div>
                <button type="button" onClick={() => setForm((f) => ({ ...f, bank_accounts: f.bank_accounts.filter((_, j) => j !== i) }))}
                  className="text-[#8A9A91] hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1"><Label className="text-xs">Label</Label>
                  <Input data-testid={`qc-vendor-bank-label-${i}`} placeholder="e.g. Primary EUR" value={b.label} onChange={(e) => setBank(i, "label", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">Beneficiary Bank Name<span className="ml-0.5 font-semibold text-red-600">*</span></Label>
                  <Input data-testid={`qc-vendor-bank-name-${i}`} value={b.beneficiary_bank_name} onChange={(e) => setBank(i, "beneficiary_bank_name", e.target.value)}
                    onBlur={(e) => setBank(i, "beneficiary_bank_name", titleCase(e.target.value))} /></div>
                <div className="space-y-1"><Label className="text-xs">SWIFT/BIC (foreign)</Label>
                  <Input value={b.swift_bic} onChange={(e) => setBank(i, "swift_bic", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">IBAN (foreign)</Label>
                  <Input value={b.iban} onChange={(e) => setBank(i, "iban", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">Account No.<span className="ml-0.5 font-semibold text-red-600">*</span></Label>
                  <Input data-testid={`qc-vendor-bank-account-${i}`} value={b.account_no} onChange={(e) => setBank(i, "account_no", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">BCN (foreign)</Label>
                  <Input value={b.bcn} onChange={(e) => setBank(i, "bcn", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">IFSC (domestic)</Label>
                  <Input value={b.ifsc} onChange={(e) => setBank(i, "ifsc", e.target.value)} /></div>
              </div>
            </div>
          ))}
        </div>
        )}
        <Button data-testid="qc-vendor-save" onClick={save} disabled={busy} className="mt-4 w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
          {busy ? "Saving…" : pickOnly ? "Change vendor" : "Save vendor"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const BANK_ROWS = [
  ["label", "Label"], ["beneficiary_bank_name", "Beneficiary Bank Name"], ["account_no", "Account No."],
  ["ifsc", "IFSC"], ["swift_bic", "SWIFT/BIC"], ["iban", "IBAN"], ["bcn", "BCN"],
];

export function VendorDetailDialog({ vendor, open, onOpenChange, canEdit, onEdit }) {
  if (!vendor) return null;
  const rows = [
    ["Vendor Name", vendor.name], ["Address", vendor.address], ["Email", vendor.email],
    ["Website", vendor.website], ["Contact Person", vendor.contact_person],
    ["Phone Number", vendor.phone], ["Country", vendor.country],
    ["Added by", vendor.created_by_name], ["Added on", fmtDateTime(vendor.created_at)],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="qc-vendor-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{vendor.name}</DialogTitle>
        </DialogHeader>
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className={k === "Address" ? "sm:col-span-2" : ""}>
              <dt className="text-xs text-[#8A9A91]">{k}</dt>
              <dd data-testid={`qc-vendor-detail-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-medium">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 space-y-3">
          <Label>Bank Accounts ({vendor.bank_accounts.length})</Label>
          {vendor.bank_accounts.map((b, i) => (
            <div key={b.id || i} data-testid={`qc-vendor-detail-bank-${i}`} className="rounded-xl border border-[#E8E6E1] p-4">
              <p className="mb-2 text-sm font-semibold">
                {b.label || b.beneficiary_bank_name} {b.is_default && <span className="ml-1 rounded-full bg-[#E5A93D]/15 px-2 py-0.5 text-[10px] font-semibold text-[#7a5b12]">DEFAULT</span>}
              </p>
              <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                {BANK_ROWS.filter(([k]) => b[k]).map(([k, label]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="text-xs text-[#8A9A91]">{label}:</dt>
                    <dd data-testid={`qc-vendor-detail-bank-${i}-${k}`} className="font-medium">{b[k]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          {vendor.bank_accounts.length === 0 && <p className="text-sm text-[#8A9A91]">No bank accounts on record.</p>}
        </div>
        {canEdit && (
          <Button data-testid="qc-vendor-detail-edit" onClick={onEdit} className="mt-4 w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Edit vendor
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const roles = user.roles || [];
  const canManage = roles.includes("admin") || roles.includes("accounts");
  const isQc = roles.includes("qc");
  const [vendors, setVendors] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [detail, setDetail] = useState(null);
  const canEditVendor = (v) => canManage || (isQc && v.created_by === user.id && Date.now() - new Date(v.created_at).getTime() < 24 * 3600 * 1000);

  const load = () => api.get("/vendors").then((r) => setVendors(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  if (!canManage && !isQc) {
    return <p className="text-sm text-[#8A9A91]">Vendor Management is available to Admin, Unit Head, Accounts and QC.</p>;
  }

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="qc-vendors-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Master Data</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Vendor (Lab) Master</h1>
        </div>
        <Button data-testid="qc-vendors-add" onClick={() => setDialog({ open: true, initial: null })}
          className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
          <Plus className="mr-1.5 h-4 w-4" /> Add Vendor
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E8E6E1] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vendor</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Bank Accounts</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(vendors || []).map((v) => (
              <TableRow key={v.id} data-testid={`qc-vendor-row-${v.id}`} onClick={() => setDetail(v)} className="cursor-pointer hover:bg-[#1C3F2B]/[0.03]">
                <TableCell>
                  <p className="font-medium">{v.name}</p>
                  <p className="text-xs text-[#8A9A91]">{v.address}</p>
                </TableCell>
                <TableCell>{v.country}</TableCell>
                <TableCell>
                  <p className="text-sm">{v.contact_person}</p>
                  <p className="text-xs text-[#8A9A91]">{v.phone}</p>
                </TableCell>
                <TableCell className="text-sm">
                  {v.bank_accounts.map((b) => (
                    <p key={b.id} className="text-xs">
                      {b.label || b.beneficiary_bank_name} {b.is_default && <span className="text-[#E5A93D]">· default</span>}
                    </p>
                  ))}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {(canManage || (isQc && v.created_by === user.id)) && (
                    <button data-testid={`qc-vendor-edit-${v.id}`} onClick={() => setDialog({ open: true, initial: v })}
                      title={!canManage ? "You can edit vendors you added within 24 hours" : "Edit vendor"}
                      className="rounded-full p-2 text-[#8A9A91] hover:bg-[#1C3F2B]/5 hover:text-[#1C3F2B]">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {canManage && (
                    <button data-testid={`qc-vendor-delete-${v.id}`} onClick={() => remove(v)}
                      className="rounded-full p-2 text-[#8A9A91] hover:bg-red-50 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {vendors && vendors.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-[#8A9A91]">No vendors yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <VendorDetailDialog
        vendor={detail}
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        canEdit={detail ? canEditVendor(detail) : false}
        onEdit={() => {
          setDialog({ open: true, initial: detail });
          setDetail(null);
        }}
      />
      <VendorFormDialog
        open={dialog.open}
        initial={dialog.initial}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        onSaved={() => {
          setDialog({ open: false, initial: null });
          load();
        }}
      />
    </div>
  );
}
